import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, Users, Mail, Phone, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { formatBRL } from "@/lib/utils";
import { canonicalParticipants, participantTotals, validateParticipant, type PoolParticipant } from "@/lib/pool-participants";

interface Props { poolId: string; totalCost: number }
type Notice = { requestId: string; recipientIds: string[] };
const emptyForm = { name: "", email: "", phone: "", shares: 1 };

async function invoke(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("confirm-pool-participants", { body });
  if (error) {
    let message = "Não foi possível confirmar. Tente novamente.";
    try { const detail = await error.context?.json(); if (typeof detail?.error === "string") message = detail.error; } catch { /* network failure */ }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export function SharesPanel({ poolId, totalCost }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PoolParticipant[] | null>(null);
  const [baseline, setBaseline] = useState<PoolParticipant[] | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [sending, setSending] = useState(false);
  const { data: list, isLoading, isFetching, error: loadError } = useQuery({
    queryKey: ["pool-shares", poolId],
    queryFn: async () => {
      const { data, error } = await supabase.from("pool_shares").select("*").eq("pool_id", poolId).order("created_at");
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: savedConfirmation } = useQuery({
    queryKey: ["pool-participant-confirmation", poolId],
    queryFn: async () => {
      const { data, error } = await supabase.from("pools").select("participant_confirmation").eq("id", poolId).single();
      if (error) throw error;
      return data.participant_confirmation as { requestId: string; recipients: { id: string }[] } | null;
    },
  });
  useEffect(() => {
    setDraft(null); setBaseline(null); setRequestId(null); setNotice(null); setEditingId(null); setForm(emptyForm);
  }, [poolId]);
  const saved = (list ?? []).map(s => ({ id: s.id, name: s.participant_name, email: s.participant_email ?? "", phone: s.participant_phone ?? "", shares: s.shares }));
  const rows = draft ?? saved;
  const { shares: totalShares, perShare } = participantTotals(rows, totalCost);
  const changed = draft !== null && JSON.stringify(canonicalParticipants(rows)) !== JSON.stringify(canonicalParticipants(baseline ?? saved));

  function stage(next: PoolParticipant[]) {
    if (baseline === null) setBaseline(saved);
    setDraft(next); setRequestId(null);
  }
  function addOrEdit() {
    const row = { ...form, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), id: editingId ?? crypto.randomUUID() };
    const invalid = validateParticipant(row);
    if (invalid) { toast.error(invalid); return; }
    if (!editingId && rows.length >= 500) { toast.error("Confirme até 500 cotistas por bolão"); return; }
    stage(editingId ? rows.map(s => s.id === editingId ? row : s) : [...rows, row]);
    setEditingId(null); setForm(emptyForm);
  }
  async function sendNotices(batch: Notice) {
    setSending(true);
    const failed: string[] = [];
    let suppressed = 0;
    try {
      for (const recipientId of batch.recipientIds) {
        try {
          const result = await invoke({ action: "notify", poolId, requestId: batch.requestId, recipientId });
          if (result.sent === false) suppressed++;
        } catch { failed.push(recipientId); }
      }
      setNotice(failed.length ? { ...batch, recipientIds: failed } : null);
      if (failed.length) toast.warning(`Cotistas salvos. ${failed.length} aviso(s) não enviado(s); tente enviar novamente.`);
      else if (suppressed) toast.warning("Cotistas salvos. Alguns endereços não permitem receber avisos.");
      else toast.success(batch.recipientIds.length ? "Cotistas atualizados e avisos enviados" : "Cotistas atualizados");
    } finally { setSending(false); }
  }
  const confirm = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Entre na sua conta para confirmar");
      const id = requestId ?? crypto.randomUUID();
      setRequestId(id);
      const result = await invoke({ action: "confirm", poolId, requestId: id, expected: canonicalParticipants(baseline ?? saved), participants: rows });
      return { requestId: id, recipientIds: result.recipientIds as string[], withoutEmail: result.withoutEmail as number };
    },
    onSuccess: async result => {
      setDraft(null); setBaseline(null); setRequestId(null); setNotice(result);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["pool-shares", poolId] }),
        qc.invalidateQueries({ queryKey: ["pool-shares-count", poolId] }),
        qc.invalidateQueries({ queryKey: ["pool-participant-confirmation", poolId] }),
      ]);
      await sendNotices(result);
      if (result.withoutEmail) toast.info(`${result.withoutEmail} cotista(s) sem e-mail cadastrado.`);
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const togglePaid = useMutation({
    mutationFn: async ({ id, isPaid, amount }: { id: string; isPaid: boolean; amount: number }) => {
      const { error } = await supabase.from("pool_shares").update({ is_paid: isPaid, amount_paid: isPaid ? amount : 0 }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pool-shares", poolId] }),
    onError: () => toast.error("Não foi possível atualizar o pagamento"),
  });
  const busy = confirm.isPending || sending || isFetching || togglePaid.isPending;
  const retry = notice ?? (savedConfirmation ? { requestId: savedConfirmation.requestId, recipientIds: savedConfirmation.recipients.map(r => r.id) } : null);
  const cancel = () => { setDraft(null); setBaseline(null); setRequestId(null); setEditingId(null); setForm(emptyForm); };

  return <Card>
    <CardHeader><CardTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> Cotistas</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <div className="grid gap-3 border border-lt-border bg-lt-bg2 p-3 sm:grid-cols-3">
        <div><div className="text-xs text-muted-foreground">Cotistas{changed ? " · pendente" : ""}</div><div className="text-lg font-medium">{rows.length}</div></div>
        <div><div className="text-xs text-muted-foreground">Total de cotas</div><div className="text-lg font-medium">{totalShares}</div></div>
        <div><div className="text-xs text-muted-foreground">Valor por cota</div><div className="text-lg font-medium">{formatBRL(perShare)}</div></div>
      </div>
      {loadError && <div role="alert" className="text-sm text-destructive">Não foi possível carregar os cotistas. <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ["pool-shares", poolId] })}>Tentar novamente</Button></div>}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_150px_90px_auto]">
        <div><Label htmlFor="participant-name" className="text-xs">Nome</Label><Input id="participant-name" disabled={busy || !!loadError} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome do cotista" /></div>
        <div><Label htmlFor="participant-email" className="text-xs">E-mail (avisos)</Label><Input id="participant-email" disabled={busy || !!loadError} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" /></div>
        <div><Label htmlFor="participant-phone" className="text-xs">Celular</Label><Input id="participant-phone" disabled={busy || !!loadError} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(00) 00000-0000" /></div>
        <div><Label htmlFor="participant-shares" className="text-xs">Cotas</Label><Input id="participant-shares" disabled={busy || !!loadError} type="number" min={1} max={100000} value={form.shares} onChange={e => setForm({ ...form, shares: Number(e.target.value) })} /></div>
        <div className="flex items-end gap-1"><Button onClick={addOrEdit} disabled={busy || !!loadError}>{editingId ? <Check className="mr-1 h-4 w-4" /> : <Plus className="mr-1 h-4 w-4" />}{editingId ? "Aplicar" : "Adicionar"}</Button>{editingId && <Button variant="ghost" size="icon" aria-label="Cancelar edição" onClick={() => { setEditingId(null); setForm(emptyForm); }}><X className="h-4 w-4" /></Button>}</div>
      </div>
      {changed && <div className="flex flex-wrap items-center justify-between gap-3 border border-lt-border bg-lt-bg2 p-3" role="status">
        <span className="text-sm">Alterações de cotistas pendentes · {rows.length} cotista(s), {totalShares} cota(s)</span>
        <div className="flex flex-wrap gap-2"><Button variant="outline" disabled={busy} onClick={cancel}>Cancelar alterações</Button><Button disabled={busy || editingId !== null || Boolean(form.name || form.email || form.phone)} onClick={() => confirm.mutate()}><Check className="mr-1 h-4 w-4" />{confirm.isPending ? "Confirmando…" : sending ? "Enviando avisos…" : "Atualizar cotistas"}</Button></div>
      </div>}
      {!changed && retry && retry.recipientIds.length > 0 && <Button variant="outline" size="sm" disabled={busy} onClick={() => sendNotices(retry)}><Mail className="mr-1 h-4 w-4" />{sending ? "Enviando avisos…" : "Reenviar avisos da confirmação"}</Button>}
      {isLoading && <div className="text-sm text-muted-foreground">Carregando cotistas…</div>}
      {!isLoading && rows.length === 0 && <div className="border border-dashed border-lt-border p-6 text-center text-sm text-muted-foreground">Nenhum cotista.</div>}
      <div className="space-y-2">{rows.map(s => {
        const stored = list?.find(x => x.id === s.id);
        const phone = s.phone.replace(/\D/g, "");
        const pending = !stored || s.name !== stored.participant_name || s.email !== (stored.participant_email ?? "") || s.phone !== (stored.participant_phone ?? "") || s.shares !== stored.shares;
        return <div key={s.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-lt-border bg-card p-3">
          <div className="min-w-0 flex-1"><div className="text-sm font-medium">{s.name}{pending && <span className="ml-2 text-xs text-muted-foreground">Pendente</span>}</div><div className="text-xs text-muted-foreground">{s.shares} cota{s.shares > 1 ? "s" : ""} • {formatBRL(perShare * s.shares)}</div><div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">{s.email && <a className="inline-flex items-center gap-1 break-all hover:text-foreground" href={`mailto:${s.email}`}><Mail className="h-3 w-3 shrink-0" />{s.email}</a>}{phone && <a className="inline-flex items-center gap-1 hover:text-foreground" href={`https://wa.me/${phone.length <= 11 ? `55${phone}` : phone}`} target="_blank" rel="noreferrer"><Phone className="h-3 w-3" />{s.phone}</a>}</div></div>
          <div className="flex items-center gap-2"><Label className="text-xs text-muted-foreground">Pago</Label><Switch aria-label={`Pagamento de ${s.name}`} checked={stored?.is_paid ?? false} disabled={busy || changed || !stored} onCheckedChange={isPaid => togglePaid.mutate({ id: s.id, isPaid, amount: perShare * s.shares })} /></div>
          <Button variant="ghost" size="icon" aria-label={`Editar cotista ${s.name}`} disabled={busy} onClick={() => { setEditingId(s.id); setForm({ name: s.name, email: s.email, phone: s.phone, shares: s.shares }); }}><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" aria-label={`Excluir cotista ${s.name}`} disabled={busy} onClick={() => { stage(rows.filter(x => x.id !== s.id)); if (editingId === s.id) { setEditingId(null); setForm(emptyForm); } }}><Trash2 className="h-4 w-4" /></Button>
        </div>;
      })}</div>
    </CardContent>
  </Card>;
}
